/**
 * Settings Route
 * V2 settings page with tab navigation (Security | App).
 * Identity and account editing lives in /profile/edit; settings keeps only
 * non-identity items (session security, app preferences).
 */

import { createFileRoute } from '@tanstack/react-router';
import { v2 } from '@/lib/i18n/v2';
import { ErrorState } from '@/components/v2/error-state';
import { SettingsTemplatePending } from '@/components/v2/page-template-pending';
import { createProfileRetry } from './-profile-retry';

type TabId = 'security' | 'app';

export function createSettingsErrorComponent(invalidate: () => unknown) {
  return ({ reset }: { reset: () => void }) => (
    <div role="status">
      <ErrorState
        title={v2.error.loadFailed('account settings')}
        onRetry={createProfileRetry(reset, invalidate)}
      />
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/profile/settings')({
  // Template-first pending: while this route's component chunk downloads, show the
  // destination's page shape instead of the full-page RoutePending mascot
  // (audit part 2: a detail click must switch immediately, skeletons included).
  // The page opens on the announcement/onboarding alpha cards + App|Security
  // switcher, not a plain row list.
  pendingComponent: SettingsTemplatePending,
  // Tab is deep-linkable via ?tab=; absent or unknown values resolve to App.
  validateSearch: (search: Record<string, unknown>): { tab?: TabId } => {
    const tab = search.tab;
    return typeof tab === 'string' && tab === 'security' ? { tab: 'security' } : {};
  },
});
